import React, { useState } from 'react';
import { Mail, Check, ArrowRight, FileText } from 'lucide-react';
import { TOKENS, limeBtn, displayFont } from '../../lib/designTokens';
import { trackGenerateLead } from '../../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

// Lead magnet catalog — one entry per audience.
// PDFs ARE wired now: stored at /app/frontend/public/dojo/<file>.pdf (Resend
// also emails the link). Direct download surfaces in the success state below.
const MAGNETS = [
  {
    id: 'peptide_cheatsheet',
    headline: 'The Peptide Protocol Cheat-Sheet',
    subhead: '8-page PDF: BPC-157, TB-500, Retatrutide and GHK-Cu dosing windows, reconstitution math and stack guidance — based on 200+ PubMed sources.',
    cta: 'Send me the cheat-sheet',
    pdfUrl: '/dojo/peptide-cheatsheet.pdf',
    pillLabel: 'Free Peptide Cheatsheet',
  },
  {
    id: 'sub60_hyrox',
    headline: 'The Sub-60 HYROX Plan',
    subhead: '12-week training plan from elite HYROX coaches — pace tables for every station, plus the strength accessories that get you under 60 minutes.',
    cta: 'Get the training plan',
    pdfUrl: '/dojo/sub60-hyrox-plan.pdf',
    pillLabel: 'Free Sub-60 HYROX Plan',
  },
  {
    id: 'wolverine_recovery',
    headline: 'The Wolverine Recovery Protocol',
    subhead: '6-page BPC-157 + TB-500 stacking guide: 4-week acute + 8-week chronic protocols, reconstitution math, injection technique, bloodwork checkpoints. The advanced athlete\u2019s healing stack.',
    cta: 'Send me the recovery stack',
    pdfUrl: '/dojo/wolverine-recovery-protocol.pdf',
    pillLabel: 'Free Wolverine Recovery Stack',
  },
];

// Inline lead-magnet section embedded directly in the homepage funnel.
// `onSuccess({ email, magnet_id, variant })` is fired exactly once after the
// POST returns 200, so callers (e.g. SoftLeadMagnetGate) can persist a
// "user already claimed this magnet" suppression flag.
export const LeadMagnetCapture = ({ magnet = MAGNETS[0], variant = 'section', onSuccess }) => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/leads/dojo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          magnet_id: magnet.id,
          source: `home_lead_magnet_${variant}`,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSubmitted(true);
      trackGenerateLead(`home_lead_magnet_${variant}`, magnet.id);
      if (onSuccess) {
        try { onSuccess({ email, magnet_id: magnet.id, variant }); }
        catch (cbErr) { console.error('LeadMagnetCapture onSuccess threw:', cbErr); }
      }
    } catch (err) {
      console.error('Lead magnet submit failed:', err);
      setError('Something went wrong — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const wrapperBg = variant === 'section' ? TOKENS.surface1 : 'transparent';

  return (
    <section
      className={variant === 'section' ? 'py-16 md:py-24' : ''}
      style={{ backgroundColor: wrapperBg }}
      data-testid={`lead-magnet-${variant}`}
    >
      <div className={variant === 'section' ? 'container-ninja' : ''}>
        <div
          className="relative overflow-hidden border max-w-4xl mx-auto"
          style={{
            backgroundColor: TOKENS.surface2,
            borderColor: TOKENS.limeBorder,
          }}
        >
          {/* Lime spotlight */}
          <div
            className="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl opacity-[0.08]"
            style={{ backgroundColor: TOKENS.lime }}
          />

          <div className="relative p-6 md:p-10 grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-10 items-center">
            {/* Left: copy */}
            <div className="md:col-span-3">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-4 h-4" style={{ color: TOKENS.lime }} />
                <span
                  className="text-[10px] uppercase tracking-[0.25em]"
                  style={{ color: TOKENS.lime, ...displayFont }}
                >
                  Free download · No spam
                </span>
              </div>
              <h3
                className="text-white text-2xl md:text-3xl lg:text-4xl mb-3 leading-tight"
                style={displayFont}
                data-testid="lead-magnet-headline"
              >
                {magnet.headline}
              </h3>
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed">
                {magnet.subhead}
              </p>
            </div>

            {/* Right: form / success */}
            <div className="md:col-span-2">
              {submitted ? (
                <div
                  className="flex flex-col items-start gap-3 p-5"
                  style={{ backgroundColor: 'rgba(199,250,62,0.08)', border: `1px solid ${TOKENS.limeBorder}` }}
                  data-testid="lead-magnet-success"
                >
                  <Check className="w-6 h-6" style={{ color: TOKENS.lime }} />
                  <p className="text-white text-sm font-medium">You&apos;re in.</p>
                  <p className="text-zinc-400 text-xs leading-relaxed">
                    We&apos;ve emailed {email} a copy. Or grab it now:
                  </p>
                  {magnet.pdfUrl && (
                    <a
                      href={magnet.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider transition-all hover:opacity-90"
                      style={limeBtn}
                      data-testid="lead-magnet-download-link"
                    >
                      Download PDF
                      <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3" data-testid="lead-magnet-form">
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full pl-11 pr-4 py-4 bg-black/40 border border-zinc-700 text-white text-sm focus:outline-none focus:border-[#C7FA3E] transition-colors"
                      disabled={submitting}
                      data-testid="lead-magnet-email-input"
                      required
                    />
                  </div>
                  {error && (
                    <p className="text-red-400 text-xs" data-testid="lead-magnet-error">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 text-sm uppercase tracking-wider transition-all duration-300 hover:opacity-90 disabled:opacity-50"
                    style={limeBtn}
                    data-testid="lead-magnet-submit"
                  >
                    {submitting ? 'Sending...' : magnet.cta}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-[10px] text-zinc-500 leading-relaxed">
                    By subscribing you agree to receive emails from NinjAthlete. Unsubscribe any time.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { MAGNETS };
